import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import useFeature from '@/contexts/FeatureFlags/useFeature';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import { postAuth } from '@/api/ApiCalls';
import { SystemMessageAudience, SystemMessageDtoIn } from '@/models/in/SystemMessageDtoIn';
import { LIVE_SYSTEM_MESSAGES_URL } from '@/pages/ManagementArea/SystemMessages/systemMessages';
import { sidebarItems, SidebarItem } from '@/components/sidebar/sidebarData';
import { managementAreaSidebarItems, ManagementAreaSidebarItem } from '@/pages/ManagementArea/managementAreaSidebarData';

/** Must match FeatureFlagKeys.FeatureAnnouncements on the server and SeedFeatureFlag_FeatureAnnouncements.sql. */
export const FEATURE_ANNOUNCEMENTS_KEY = 'feature-announcements';

/** Where an announcement's chip may appear: the fund menu is the coordinators', the management menu the office's. */
export type AnnouncementPlace = 'fund' | 'management';

interface AnnouncementsContextValue {
  /** Whether chips and bubbles are on for this user at all. */
  isOn: boolean;
  /** The unread announcements that belong to a feature flag, oldest news first. */
  unread: SystemMessageDtoIn[];
  /** Whether any of these flags has an unread announcement meant for this place. */
  hasUnread: (featureKeys: (string | undefined)[], place: AnnouncementPlace) => boolean;
  markRead: (ids: number[]) => Promise<void>;
  /** Marks an element as the place a bubble step may point at. Returns the undo. */
  registerAnchor: (name: string, element: HTMLElement) => () => void;
  /** The elements currently marked with this name, in the order they registered. */
  anchorsNamed: (name: string) => HTMLElement[];
  /** The bubble on screen now, if any. Picked by AnnouncementTour, drawn by the anchor it points at. */
  bubble: AnnouncementBubbleState | null;
  setBubble: (bubble: AnnouncementBubbleState | null) => void;
}

/**
 * The one bubble on screen: which message, which step, which element, and what its buttons do.
 *
 * Drawn by the AnnouncementAnchor that owns the element rather than at the top of the app, so the
 * bubble belongs to the same React tree as what it points at. An anchor inside a dialog then has its
 * bubble inside that dialog's tree, where clicking it is not "outside the dialog" and does not close it.
 */
export interface AnnouncementBubbleState {
  message: SystemMessageDtoIn;
  stepIndex: number;
  element: HTMLElement;
  /** The next step's element is on this screen now. Without it the bubble ends at this step. */
  nextHere: boolean;
  advance: () => void;
  finish: () => void;
  snooze: () => void;
}

const AnnouncementsContext = createContext<AnnouncementsContextValue | null>(null);

const audienceFits = (audience: SystemMessageAudience, place: AnnouncementPlace) => (place === 'fund' ? audience !== 'office' : audience !== 'coordinators');

/** Oldest news first: the bubble that waited longest is the one shown first. */
const newsMoment = (message: SystemMessageDtoIn) => message.publishedAt ?? message.updatedAt ?? message.createdAt;

/**
 * The feature keys a route belongs to, read from the two menus: visiting a new screen is what
 * clears its «חדש» chip. Fund routes are relative to the fund (`support/supporteds`), the
 * management area's are absolute.
 */
const routesByFeature = (() => {
  const map = new Map<string, { path: string; place: AnnouncementPlace }[]>();
  const add = (key: string | undefined, path: string | undefined, place: AnnouncementPlace) => {
    if (!key || !path) return;
    map.set(key, [...(map.get(key) ?? []), { path, place }]);
  };
  const walkFund = (items: SidebarItem[]) =>
    items.forEach((item) => {
      add(item.featureKey, item.to, 'fund');
      if (item.children) walkFund(item.children);
    });
  const walkManagement = (items: ManagementAreaSidebarItem[]) =>
    items.forEach((item) => {
      add(item.featureKey, item.to, 'management');
      if (item.children) walkManagement(item.children);
    });
  walkFund(sidebarItems);
  walkManagement(managementAreaSidebarItems);
  return map;
})();

const isOnRoute = (pathname: string, route: { path: string; place: AnnouncementPlace }) => {
  if (route.path.startsWith('/')) return pathname === route.path || pathname.startsWith(`${route.path}/`);
  // /:fundId/<path>
  const withinFund = pathname.split('/').filter(Boolean).slice(1).join('/');
  return withinFund === route.path || withinFund.startsWith(`${route.path}/`);
};

/**
 * The announcements that belong to feature flags, for the chips in the menus and the bubbles on the
 * screens (HAT-1489).
 *
 * One fetch for the whole app, shared with the bell and the sign-in popup: the same URL, so the same
 * react-query entry. Marking one read refetches it, and the bell's count drops with the chip.
 *
 * Mounted in Layout, above the routes and outside FundProvider - the header and the management area
 * read it too. The server has already decided which announcements are this user's (the flag is open
 * to them somewhere, the audience fits), so all that is left here is where to show them.
 */
export function AnnouncementsProvider({ children }: { children: ReactNode }) {
  const { currentUser } = useAuthContext();
  const isOn = useFeature(FEATURE_ANNOUNCEMENTS_KEY);
  const { pathname } = useLocation();

  const { data, refetch } = useGetAuthApi<SystemMessageDtoIn[]>(currentUser && isOn ? LIVE_SYSTEM_MESSAGES_URL : '');

  const unread = useMemo(
    () => (data ?? []).filter((message) => message.featureFlagKey && !message.isRead).sort((a, b) => newsMoment(a).localeCompare(newsMoment(b))),
    [data]
  );

  const hasUnread = useCallback(
    (featureKeys: (string | undefined)[], place: AnnouncementPlace) =>
      isOn && unread.some((message) => featureKeys.includes(message.featureFlagKey ?? undefined) && audienceFits(message.audience, place)),
    [isOn, unread]
  );

  /*
   * What this session already marked, so a slow refetch cannot bring a chip or a bubble back for a
   * moment, and a failed POST does not keep re-sending on every route change.
   */
  const marked = useRef<Set<number>>(new Set());

  const markRead = useCallback(
    async (ids: number[]) => {
      const fresh = ids.filter((id) => !marked.current.has(id));
      if (fresh.length === 0) return;
      fresh.forEach((id) => marked.current.add(id));
      const headers = { Authorization: `Bearer ${currentUser?.token}` };
      try {
        await Promise.all(fresh.map((id) => postAuth<void>(`system-messages/${id}/read`, headers, {})));
        // Its own refetch, not invalidateQueries - see UpdatesBell for why.
        await refetch();
      } catch {
        // Worst case the chip comes back on the next load; nothing useful to show for it here.
      }
    },
    [currentUser?.token, refetch]
  );

  /*
   * Visiting a new screen is reading its announcement - unless the announcement has a bubble, which
   * the bubble itself marks when it is dismissed or its element is used.
   */
  useEffect(() => {
    const visited = unread.filter(
      (message) =>
        message.tour.length === 0 &&
        (routesByFeature.get(message.featureFlagKey!) ?? []).some((route) => audienceFits(message.audience, route.place) && isOnRoute(pathname, route))
    );
    if (visited.length > 0) void markRead(visited.map((message) => message.id));
  }, [pathname, unread, markRead]);

  /*
   * The anchors live in a ref, not in state: registering one must not re-render the app. The bubble
   * looks them up on its own schedule.
   */
  const anchors = useRef(new Map<string, HTMLElement[]>());
  const registerAnchor = useCallback((name: string, element: HTMLElement) => {
    const list = anchors.current.get(name) ?? [];
    if (!list.includes(element)) anchors.current.set(name, [...list, element]);
    return () => {
      const current = anchors.current.get(name) ?? [];
      const rest = current.filter((el) => el !== element);
      if (rest.length > 0) anchors.current.set(name, rest);
      else anchors.current.delete(name);
    };
  }, []);
  const anchorsNamed = useCallback((name: string) => anchors.current.get(name) ?? [], []);

  const [bubble, setBubble] = useState<AnnouncementBubbleState | null>(null);

  const value = useMemo(
    () => ({ isOn, unread: isOn ? unread : [], hasUnread, markRead, registerAnchor, anchorsNamed, bubble, setBubble }),
    [isOn, unread, hasUnread, markRead, registerAnchor, anchorsNamed, bubble]
  );

  return <AnnouncementsContext.Provider value={value}>{children}</AnnouncementsContext.Provider>;
}

/** Safe outside the provider: nothing is ever new there. */
export function useAnnouncements(): AnnouncementsContextValue | null {
  return useContext(AnnouncementsContext);
}
